import createClient from "openapi-fetch";
import type { paths } from "./types";
import { useImpersonationStore } from "../auth/useImpersonationStore";

// Typed fetch client wrapping the orchestrator. baseUrl is set at build
// time from VITE_API_BASE_URL; falls back to same-origin in dev (rely on
// vite proxy or local orchestrator on :8080 via env).
//
// credentials: 'include' is required for cookie auth (decision 14 in the
// 0005 plan: cookie-only auth in v1; pmt_session is HttpOnly so the SPA
// never sees the value, browser sends automatically as same-site).
const baseUrl =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== "undefined" ? window.location.origin : "");

export const apiClient = createClient<paths>({
  baseUrl,
  credentials: "include",
  // Inject CSRF gate header on mutating requests. The orchestrator's auth
  // middleware requires X-Requested-With: xhr on cookie-authed POST/PATCH/
  // DELETE; bearer-authed requests skip this check (we ship cookie-only in
  // v1, but sending the header on bearer requests is harmless).
  headers: {
    "X-Requested-With": "xhr",
  },
});

// 0016 Slice 3 — read the impersonation banner headers off EVERY
// response and propagate to the impersonation store. The auth
// middleware stamps these on every response that's honored via the
// pmt_session_admin cookie, so we don't need to make a special call;
// just observing /me / /products / etc is enough.
apiClient.use({
  onResponse({ response }) {
    const target = response.headers.get("X-Impersonating-User-ID");
    const admin = response.headers.get("X-Impersonator-Admin-ID");
    const store = useImpersonationStore.getState();
    if (target) {
      if (!store.active || store.targetUID !== target) {
        store.set(true, target, admin);
      }
    } else if (store.active) {
      // No header on this response — but the impersonation cookie may
      // still be live on a different request. Don't clear here on
      // every non-impersonation response; the store gets cleared
      // explicitly via Stop-impersonating + the cookie's own expiry.
      //
      // BUT: /me with no X-Impersonating-User-ID after we previously
      // saw one means the impersonation just ended (cookie expired,
      // or the user clicked Stop). Use the request URL to gate.
      if (response.url.endsWith("/api/v1/me")) {
        store.clear();
      }
    }
    return undefined;
  },
});
